<script>
  import { onMount } from "svelte";
  import {
    Page,
    Block,
    Row,
    Col,
    Button,
    Card,
    CardContent,
    Preloader,
    f7,
    f7ready,
  } from "framework7-svelte";

  import AdminNavbar from "../components/common/AdminNavbar.svelte";
  import KpiStatCard from "../components/common/KpiStatCard.svelte";
  import StatusBadge from "../components/common/StatusBadge.svelte";
  import CsvExportButton from "../components/common/CsvExportButton.svelte";

  import { adminQuery } from "../services/adminGQL";
  import { sanitizeRecords } from "../utils/dataSanitizer";
  import { formatINR, formatDateTime } from "../utils/formatters";

  export let f7router;
  export let f7route;

  const TEST_MOBILES = ["9599699598", "9747332795", "9876543210"];
  const TEST_NAMES = ["amal rag c", "ricky jacob", "test user", "test"];

  let rawTransactions = [];
  let loading = false;
  let categoryFilter = "SUCCESS"; // 'SUCCESS' | 'FAILED' | 'ALL'
  let filterTestAccounts = true;
  let searchQuery = "";

  onMount(() => {
    f7ready(() => {
      fetchTransactions();
    });
  });

  async function fetchTransactions() {
    loading = true;
    try {
      // If ALL is selected, backend query category can be omitted or 'ALL'
      const variables = categoryFilter === "ALL" ? {} : { category: categoryFilter };
      const res = await adminQuery(
        "fetch_cashfree_transactions",
        variables,
        "cashfree_transactions { user_name mobile account_number ifsc bank_name created_ts orderId orderAmount txStatus referenceId paymentMode txMsg txTime }"
      );

      const list = res?.data?.fetch_cashfree_transactions?.cashfree_transactions || [];
      rawTransactions = sanitizeRecords(list, "");
    } catch (err) {
      console.error("Cashfree fetch error:", err);
      f7.dialog.alert(err.message || "Failed to fetch Cashfree transactions", "API Error");
    } finally {
      loading = false;
    }
  }

  function isTestRecord(tx) {
    if (!tx) return false;
    const mob = String(tx.mobile || "").trim();
    const name = String(tx.user_name || "").trim().toLowerCase();
    if (TEST_MOBILES.some((tm) => mob.includes(tm))) return true;
    if (TEST_NAMES.some((tn) => name.includes(tn))) return true;
    return false;
  }

  // Reactive filtering
  $: filteredTransactions = rawTransactions.filter((tx) => {
    // 1. Filter test accounts if toggled
    if (filterTestAccounts && isTestRecord(tx)) {
      return false;
    }

    // 2. Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const matchName = String(tx.user_name || "").toLowerCase().includes(q);
      const matchMobile = String(tx.mobile || "").includes(q);
      const matchOrderId = String(tx.orderId || "").toLowerCase().includes(q);
      const matchRefId = String(tx.referenceId || "").toLowerCase().includes(q);
      return matchName || matchMobile || matchOrderId || matchRefId;
    }

    return true;
  });

  // Calculate KPIs
  $: testRecordsCount = rawTransactions.filter(isTestRecord).length;
  $: successfulTransactions = filteredTransactions.filter((tx) =>
    String(tx.txStatus || "").toUpperCase().includes("SUCCESS")
  );
  $: failedTransactions = filteredTransactions.filter((tx) =>
    String(tx.txStatus || "").toUpperCase().includes("FAIL")
  );
  $: totalVolume = successfulTransactions.reduce((acc, curr) => {
    const val = parseFloat(curr.orderAmount);
    return acc + (isNaN(val) ? 0 : val);
  }, 0);

  // CSV export configuration
  const exportColumns = [
    { header: "Order ID", key: "orderId" },
    { header: "Reference ID", key: "referenceId" },
    { header: "User Name", key: "user_name" },
    { header: "Mobile", key: "mobile" },
    { header: "Amount (INR)", key: "orderAmount" },
    { header: "Tx Status", key: "txStatus" },
    { header: "Payment Mode", key: "paymentMode" },
    { header: "Tx Time", key: "txTime" },
    { header: "Bank Name", key: "bank_name" },
    { header: "Account Number", key: "account_number" },
    { header: "IFSC", key: "ifsc" },
    { header: "Tx Message", key: "txMsg" },
  ];

  function handleCategoryChange(newCat) {
    if (categoryFilter === newCat) return;
    categoryFilter = newCat;
    fetchTransactions();
  }
</script>

<Page class="cashfree-transactions-page">
  <AdminNavbar title="Cashfree Payment Reconciliation" subtitle="Task 4: Payment Audit & Gateway Reconciliation" />

  <div class="page-content-wrapper padding">
    <!-- Summary KPI Row -->
    <Row class="margin-bottom">
      <Col width="100" medium="25">
        <KpiStatCard
          title="Settled Volume"
          value={formatINR(totalVolume)}
          subtitle="{successfulTransactions.length} successful orders"
          icon="f7:money_dollar_circle_fill"
          iconColor="#10b981"
          theme="success"
        />
      </Col>
      <Col width="100" medium="25">
        <KpiStatCard
          title="Total Transactions"
          value={filteredTransactions.length}
          subtitle="Showing {categoryFilter} category"
          icon="f7:doc_plaintext"
          iconColor="#2563eb"
          theme="default"
        />
      </Col>
      <Col width="100" medium="25">
        <KpiStatCard
          title="Failed / Pending"
          value={failedTransactions.length}
          subtitle="Requires operator audit"
          icon="f7:exclamationmark_triangle_fill"
          iconColor="#ef4444"
          theme="danger"
        />
      </Col>
      <Col width="100" medium="25">
        <KpiStatCard
          title="Test Accounts Hidden"
          value={testRecordsCount}
          subtitle={filterTestAccounts ? "Active filter applied" : "Filter turned off"}
          icon="f7:shield_lefthalf_fill"
          iconColor="#8b5cf6"
          theme="purple"
          clickable={true}
          active={filterTestAccounts}
          on:click={() => (filterTestAccounts = !filterTestAccounts)}
        />
      </Col>
    </Row>

    <!-- Controls & Action Toolbar -->
    <div class="controls-toolbar margin-bottom">
      <!-- Category Switcher Segment -->
      <div class="category-segment">
        <div class="segmented segmented-raised">
          <Button
            active={categoryFilter === "SUCCESS"}
            on:click={() => handleCategoryChange("SUCCESS")}
            class="seg-btn"
          >
            <span>Success</span>
          </Button>
          <Button
            active={categoryFilter === "FAILED"}
            on:click={() => handleCategoryChange("FAILED")}
            class="seg-btn"
          >
            <span>Failed</span>
          </Button>
          <Button
            active={categoryFilter === "ALL"}
            on:click={() => handleCategoryChange("ALL")}
            class="seg-btn"
          >
            <span>All Categories</span>
          </Button>
        </div>
      </div>

      <!-- Test Account Filter Toggle Button -->
      <div class="test-filter-toggle">
        <Button
          outline={!filterTestAccounts}
          fill={filterTestAccounts}
          color="purple"
          small
          on:click={() => (filterTestAccounts = !filterTestAccounts)}
          class="test-toggle-btn"
        >
          <span>{filterTestAccounts ? "✓ Hiding Test Users" : "Showing Test Users"}</span>
          <span class="count-pill">{testRecordsCount}</span>
        </Button>
      </div>

      <!-- Search Box -->
      <div class="search-input-wrap">
        <input
          type="text"
          placeholder="Filter by name, mobile, order ID..."
          bind:value={searchQuery}
          class="search-input"
        />
        {#if searchQuery}
          <button class="clear-search" on:click={() => (searchQuery = "")}>✕</button>
        {/if}
      </div>

      <!-- One-Click RFC-4180 CSV Export -->
      <div class="export-wrap">
        <CsvExportButton
          filename="cashfree_transactions_{categoryFilter.toLowerCase()}_{new Date().toISOString().slice(0, 10)}.csv"
          columns={exportColumns}
          data={filteredTransactions}
          label="Export Audit CSV"
        />
      </div>

      <!-- Refresh Button -->
      <Button small outline color="blue" on:click={fetchTransactions} disabled={loading}>
        {#if loading}
          <Preloader size={14} />
        {:else}
          <span>🔄 Refresh</span>
        {/if}
      </Button>
    </div>

    <!-- Data Table Card -->
    <Card class="table-card">
      <CardContent class="no-padding">
        {#if loading}
          <div class="loading-state">
            <Preloader size={32} />
            <p>Loading Cashfree transactions from gateway...</p>
          </div>
        {:else if filteredTransactions.length === 0}
          <div class="empty-state">
            <span class="empty-icon">📂</span>
            <p>No transactions found matching the selected filters.</p>
          </div>
        {:else}
          <div class="data-table-responsive">
            <table class="cf-table">
              <thead>
                <tr>
                  <th style="width: 50px;">#</th>
                  <th>Customer</th>
                  <th>Mobile</th>
                  <th>Order / Ref ID</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Mode</th>
                  <th>Timestamp</th>
                  <th>Bank Info</th>
                </tr>
              </thead>
              <tbody>
                {#each filteredTransactions as tx, i}
                  <tr class:test-row={isTestRecord(tx)}>
                    <td class="col-index">{i + 1}</td>
                    <td class="col-name">
                      <div class="user-name-cell">
                        <span class="name-text">{tx.user_name || "Unknown"}</span>
                        {#if isTestRecord(tx)}
                          <span class="test-indicator">TEST</span>
                        {/if}
                      </div>
                    </td>
                    <td class="col-mobile">{tx.mobile || "-"}</td>
                    <td class="col-ids">
                      <div class="id-cell">
                        <span class="order-id" title={tx.orderId}>{tx.orderId || "-"}</span>
                        {#if tx.referenceId}
                          <span class="ref-id" title={tx.referenceId}>Ref: {tx.referenceId}</span>
                        {/if}
                      </div>
                    </td>
                    <td class="col-amount">
                      <span class="amount-text">{formatINR(tx.orderAmount)}</span>
                    </td>
                    <td class="col-status">
                      <StatusBadge status={tx.txStatus} size="small" />
                    </td>
                    <td class="col-mode">
                      <span class="mode-pill">{tx.paymentMode || "-"}</span>
                    </td>
                    <td class="col-time">
                      <span class="time-text">{tx.txTime || formatDateTime(tx.created_ts)}</span>
                    </td>
                    <td class="col-bank">
                      {#if tx.bank_name || tx.ifsc}
                        <div class="bank-cell">
                          <span class="bank-name">{tx.bank_name || "-"}</span>
                          <span class="bank-ifsc">{tx.ifsc || ""}</span>
                        </div>
                      {:else}
                        <span class="text-muted">-</span>
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </CardContent>
    </Card>
  </div>
</Page>

<style>
  .page-content-wrapper {
    max-width: 1400px;
    margin: 0 auto;
  }

  .controls-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #ffffff;
    padding: 12px 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    flex-wrap: wrap;
  }

  .category-segment {
    min-width: 240px;
  }

  .seg-btn {
    font-size: 13px;
    font-weight: 600;
    text-transform: none;
    padding: 6px 14px;
    height: auto;
  }

  .test-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    text-transform: none;
    height: 32px;
  }

  .count-pill {
    background: rgba(0, 0, 0, 0.15);
    padding: 1px 6px;
    border-radius: 10px;
    font-size: 11px;
  }

  .search-input-wrap {
    flex: 1;
    min-width: 200px;
    position: relative;
    display: flex;
    align-items: center;
  }

  .search-input {
    width: 100%;
    height: 34px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 0 28px 0 12px;
    font-size: 13px;
    color: #0f172a;
    background: #f8fafc;
  }

  .search-input:focus {
    background: #ffffff;
    border-color: #3b82f6;
    outline: none;
  }

  .clear-search {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    font-size: 12px;
    color: #94a3b8;
    cursor: pointer;
  }

  .table-card {
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;
  }

  .data-table-responsive {
    overflow-x: auto;
    width: 100%;
  }

  .cf-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    text-align: left;
  }

  .cf-table thead th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 12px 14px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }

  .cf-table tbody td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
    vertical-align: middle;
  }

  .cf-table tbody tr:hover td {
    background: #f8fafc;
  }

  .cf-table tbody tr.test-row td {
    background: #faf5ff;
  }

  .col-index {
    color: #94a3b8;
    font-size: 12px;
    font-weight: 500;
  }

  .user-name-cell {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .name-text {
    font-weight: 600;
    color: #0f172a;
  }

  .test-indicator {
    background: #ede9fe;
    color: #7c3aed;
    font-size: 10px;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 4px;
    letter-spacing: 0.3px;
  }

  .col-mobile {
    font-family: monospace;
    font-size: 12px;
    color: #475569;
  }

  .id-cell {
    display: flex;
    flex-direction: column;
    max-width: 180px;
  }

  .order-id {
    font-family: monospace;
    font-size: 12px;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ref-id {
    font-family: monospace;
    font-size: 11px;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .amount-text {
    font-weight: 700;
    color: #0f172a;
  }

  .mode-pill {
    background: #f1f5f9;
    color: #475569;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 6px;
    text-transform: uppercase;
  }

  .time-text {
    font-size: 12px;
    color: #64748b;
    white-space: nowrap;
  }

  .bank-cell {
    display: flex;
    flex-direction: column;
    font-size: 12px;
  }

  .bank-name {
    font-weight: 500;
    color: #334155;
  }

  .bank-ifsc {
    font-family: monospace;
    font-size: 11px;
    color: #94a3b8;
  }

  .text-muted {
    color: #94a3b8;
  }

  .loading-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 50px 20px;
    color: #64748b;
  }

  .empty-icon {
    font-size: 36px;
    margin-bottom: 8px;
  }
</style>
